import { useEffect, useState } from "react";
import { Banner, Card, SectionTitle, Toggle } from "../components/ui";
import { ApiError, type Api } from "../lib/api";
import type {
  AccessRequest,
  Account,
  HubSpotCompany,
  PortalUser,
  ReleaseLine,
} from "../lib/types";

const ALL_LINES: ReleaseLine[] = ["release-avx256-rl10", "release-rpm-el10"];

/**
 * Directory console — platform_admin only. The single place to manage customer
 * accounts and their users with full CRUD: create/rename/suspend/entitle/delete
 * accounts, see and manage each account's logs bucket (provision/delete), invite
 * and remove users, change in-account roles, and approve self-service access
 * requests. Accounts are portal-local (Firestore); when HubSpot linking is enabled
 * an account can also be tied to a HubSpot company for non-CUI ticket sync.
 */
export function Directory({ api }: { api: Api }) {
  return (
    <div>
      <SectionTitle
        kicker="Admin · Directory"
        title="Accounts & users"
        desc="Create and manage customer accounts, their dedicated logs buckets, and the users assigned to each. Approve self-service access requests and associate them to an account."
      />
      <div className="mt-6">
        <Accounts api={api} />
      </div>
      <div className="mt-6">
        <PendingRequests api={api} />
      </div>
    </div>
  );
}

function Accounts({ api }: { api: Api }) {
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [buckets, setBuckets] = useState<Record<string, boolean>>({});
  const [name, setName] = useState("");
  const [lines, setLines] = useState<ReleaseLine[]>([...ALL_LINES]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [hsEnabled, setHsEnabled] = useState(false);
  const [hsCompanies, setHsCompanies] = useState<HubSpotCompany[]>([]);
  // "" = no link, "__new__" = create a new HubSpot company, otherwise a company id.
  const [hsChoice, setHsChoice] = useState("");

  function load() {
    setError(null);
    api
      .listAccounts()
      .then((r) => setAccounts(r.accounts))
      .catch((err) =>
        setError(err instanceof ApiError ? err.message : "Could not load accounts."),
      );
    api
      .accountBucketStatus()
      .then((r) => setBuckets(r.status))
      .catch(() => setBuckets({}));
    // Refetched on every load, not once on mount: a company created through "Create a new
    // HubSpot company" has to become selectable for the other accounts immediately, rather
    // than only after a full page reload.
    api
      .listHubspotCompanies()
      .then((r) => {
        setHsEnabled(r.enabled);
        setHsCompanies(r.companies);
      })
      .catch(() => setHsEnabled(false));
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function create() {
    setError(null);
    setOk(null);
    if (!name.trim()) return setError("Enter a company name.");
    setBusy(true);
    try {
      const opts =
        hsEnabled && hsChoice
          ? hsChoice === "__new__"
            ? { createInHubspot: true }
            : { hubspotCompanyId: hsChoice }
          : undefined;
      const r = await api.createAccount(name.trim(), lines, opts);
      const link = r.account.hubspotCompanyId ? ` · HubSpot ${r.account.hubspotCompanyId}` : "";
      setOk(`Created ${name.trim()} · bucket ${r.account.logsBucket}${link}`);
      setName("");
      setLines([...ALL_LINES]);
      setHsChoice("");
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create account.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <div className="mb-4 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-ink">Accounts</h2>
          <p className="text-xs text-ink-dim">
            Creating an account provisions its own dedicated logs bucket. Rename, re-entitle,
            suspend, manage buckets, and assign users below.
          </p>
        </div>
        <button className="btn-ghost" onClick={load}>Refresh</button>
      </div>
      {error && <Banner tone="error">{error}</Banner>}
      {ok && <Banner tone="success">{ok}</Banner>}
      <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
        <label className="block text-sm">
          <span className="mb-1 block text-ink-muted">Company name</span>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
          />
        </label>
        <button className="btn-primary" disabled={busy || !name.trim()} onClick={create}>
          {busy ? "Provisioning…" : "Create & provision bucket"}
        </button>
      </div>
      <div className="mt-2 flex flex-wrap gap-4">
        {ALL_LINES.map((l) => (
          <label key={l} className="flex items-center gap-2 text-sm text-ink-muted">
            <input
              type="checkbox"
              checked={lines.includes(l)}
              disabled={busy}
              onChange={(e) =>
                setLines((cur) => (e.target.checked ? [...cur, l] : cur.filter((x) => x !== l)))
              }
            />
            <span className="font-mono text-xs">{l}</span>
          </label>
        ))}
      </div>
      {hsEnabled && (
        <label className="mt-3 block text-sm">
          <span className="mb-1 block text-ink-muted">HubSpot account (optional)</span>
          <select
            className="input"
            value={hsChoice}
            disabled={busy}
            onChange={(e) => setHsChoice(e.target.value)}
          >
            <option value="">Not linked to HubSpot</option>
            <option value="__new__">➕ Create a new HubSpot company</option>
            {hsCompanies.map((c) => (
              <option key={c.companyId} value={c.companyId}>
                {c.name}
                {c.domain ? ` (${c.domain})` : ""}
              </option>
            ))}
          </select>
          <span className="mt-1 block text-xs text-ink-dim">
            Link this account to a HubSpot company to mirror non-CUI support cases. CUI stays in the
            portal.
          </span>
        </label>
      )}
      <div className="mt-5">
        {accounts && accounts.length === 0 && (
          <p className="text-sm text-ink-muted">No accounts yet.</p>
        )}
        {accounts && accounts.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-line text-xs uppercase tracking-wide text-ink-dim">
                  <th className="py-2 pr-4">Account</th>
                  <th className="py-2 pr-4">Release lines</th>
                  <th className="py-2 pr-4">Logs bucket</th>
                  {hsEnabled && <th className="py-2 pr-4">HubSpot</th>}
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2">Manage</th>
                </tr>
              </thead>
              <tbody className="text-xs text-ink-muted">
                {accounts.map((a) => (
                  <AccountRow
                    key={a.companyId}
                    api={api}
                    account={a}
                    hasBucket={buckets[a.companyId] ?? false}
                    allAccounts={accounts}
                    hsEnabled={hsEnabled}
                    hsCompanies={hsCompanies}
                    onChanged={load}
                    onDeleted={load}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}

/** One account row that expands to reveal editing + the users assigned to it. */
function AccountRow({
  api,
  account,
  hasBucket,
  allAccounts,
  hsEnabled,
  hsCompanies,
  onChanged,
  onDeleted,
}: {
  api: Api;
  account: Account;
  hasBucket: boolean;
  /** Every account, so a user can be re-homed into one of the others. */
  allAccounts: Account[];
  hsEnabled: boolean;
  hsCompanies: HubSpotCompany[];
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState<PortalUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actingId, setActingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [linking, setLinking] = useState(false);
  // Inline account editing.
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(account.name);
  const [editLines, setEditLines] = useState<ReleaseLine[]>(account.releaseLines);
  const [savingAcct, setSavingAcct] = useState(false);
  const [bucketBusy, setBucketBusy] = useState(false);
  // Invite-local user form.
  const [invEmail, setInvEmail] = useState("");
  const [invName, setInvName] = useState("");
  const [invRole, setInvRole] = useState("member");
  const [inviting, setInviting] = useState(false);
  const [invLink, setInvLink] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const colSpan = hsEnabled ? 6 : 5;

  async function relink(choice: string) {
    // "" = unlink, "__new__" = create+link a new HubSpot company, else an existing id.
    setLinking(true);
    setError(null);
    try {
      if (choice === "__new__") {
        await api.linkAccountHubspot(account.companyId, null, { createInHubspot: true });
      } else {
        await api.linkAccountHubspot(account.companyId, choice === "" ? null : choice);
      }
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not update the HubSpot link.");
    } finally {
      setLinking(false);
    }
  }

  async function loadUsers() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.listAccountUsers(account.companyId);
      setUsers(r.users);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load users.");
    } finally {
      setBusy(false);
    }
  }

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (next && users === null) await loadUsers();
  }

  async function saveAccount() {
    setSavingAcct(true);
    setError(null);
    try {
      await api.updateAccount(account.companyId, { name: editName.trim(), releaseLines: editLines });
      setEditing(false);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the account.");
    } finally {
      setSavingAcct(false);
    }
  }

  async function setStatus(status: "active" | "suspended") {
    setSavingAcct(true);
    setError(null);
    try {
      await api.updateAccount(account.companyId, { status });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not change the status.");
    } finally {
      setSavingAcct(false);
    }
  }

  async function bucketAction(kind: "provision" | "delete") {
    if (kind === "delete") {
      const ok = window.confirm(
        `Delete the logs bucket for “${account.name}”? This permanently deletes all uploaded logs for this account. The account record is kept. This cannot be undone.`,
      );
      if (!ok) return;
    }
    setBucketBusy(true);
    setError(null);
    try {
      if (kind === "provision") await api.provisionBucket(account.companyId);
      else await api.deleteBucket(account.companyId);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "The bucket action could not be completed.");
    } finally {
      setBucketBusy(false);
    }
  }

  async function changeRole(u: PortalUser, role: string) {
    setActingId(u.contactId);
    setError(null);
    try {
      await api.setUserRole(u.contactId, role, account.companyId);
      await loadUsers();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not change the role.");
    } finally {
      setActingId(null);
    }
  }

  /**
   * Re-home a user into another account. The target account owns the logs bucket and the
   * entitlements, so their future uploads land there; existing cases and already-uploaded
   * logs stay with the account they were filed under.
   */
  async function moveUser(u: PortalUser, companyId: string) {
    if (!companyId || companyId === account.companyId) return;
    const target = allAccounts.find((a) => a.companyId === companyId);
    const ok = window.confirm(
      `Move ${u.email} to “${target?.name ?? companyId}”? Their future log uploads go to that ` +
        `account's bucket. Cases and logs already filed under “${account.name}” stay where they are.`,
    );
    if (!ok) return;
    setActingId(u.contactId);
    setError(null);
    try {
      await api.moveUser(u.contactId, companyId);
      await loadUsers();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not move the user.");
    } finally {
      setActingId(null);
    }
  }

  async function invite() {
    if (!invEmail.trim()) return;
    setInviting(true);
    setError(null);
    setInvLink(null);
    try {
      const r = await api.inviteLocalUser({
        email: invEmail.trim(),
        companyId: account.companyId,
        role: invRole,
        name: invName.trim() || undefined,
      });
      setInvLink(r.actionLink);
      setInvEmail("");
      setInvName("");
      setInvRole("member");
      await loadUsers();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not invite the user.");
    } finally {
      setInviting(false);
    }
  }

  /**
   * Email the user a fresh set-password link. The link goes to their mailbox only — an
   * admin never sees it, so resetting a password cannot become a way to take over an
   * account.
   */
  async function resetPassword(u: PortalUser) {
    const ok = window.confirm(
      `Email ${u.email} a link to set a new password? Their current password keeps working until they use it.`,
    );
    if (!ok) return;
    setActingId(u.contactId);
    setError(null);
    setNotice(null);
    try {
      await api.sendPasswordReset(u.contactId, account.companyId);
      setNotice(`Password-reset link emailed to ${u.email}.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not send the reset link.");
    } finally {
      setActingId(null);
    }
  }

  async function act(u: PortalUser, kind: "deactivate" | "reactivate" | "delete") {
    if (kind === "delete") {
      const ok = window.confirm(
        `Delete ${u.email} from this account? This removes their portal access and login. This cannot be undone.`,
      );
      if (!ok) return;
    }
    setActingId(u.contactId);
    setError(null);
    try {
      if (kind === "deactivate") await api.deactivateUser(u.contactId, u.uid, account.companyId);
      else if (kind === "reactivate") await api.reactivateUser(u.contactId, u.uid, account.companyId);
      else await api.deleteUser(u.contactId, u.uid, account.companyId);
      await loadUsers();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "The action could not be completed.");
    } finally {
      setActingId(null);
    }
  }

  async function deleteAccount() {
    const ok = window.confirm(
      `Delete account “${account.name}”? This permanently deletes its logs bucket and all uploaded logs. ` +
        `The account must have no users. This cannot be undone.`,
    );
    if (!ok) return;
    setDeleting(true);
    setError(null);
    try {
      await api.deleteAccount(account.companyId);
      onDeleted();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "The account could not be deleted.");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <tr className="border-b border-line/50">
        <td className="py-2 pr-4">{account.name}</td>
        <td className="py-2 pr-4 font-mono">{account.releaseLines.join(", ")}</td>
        <td className="py-2 pr-4">
          <div className="flex items-center gap-2">
            <Toggle
              checked={hasBucket}
              busy={bucketBusy}
              onChange={(next) => void bucketAction(next ? "provision" : "delete")}
              title={hasBucket ? account.logsBucket : "No bucket provisioned"}
            />
            <span
              className={`font-mono max-w-[10rem] truncate ${hasBucket ? "text-accent-strong" : "text-ink-dim"}`}
              title={account.logsBucket}
            >
              {hasBucket ? account.logsBucket : "off"}
            </span>
          </div>
        </td>
        {hsEnabled && (
          <td className="py-2 pr-4">
            <select
              className="input !py-1 text-xs"
              value={account.hubspotCompanyId ?? ""}
              disabled={linking}
              onChange={(e) => void relink(e.target.value)}
            >
              <option value="">Not linked</option>
              <option value="__new__">➕ Create a new HubSpot company</option>
              {account.hubspotCompanyId &&
                !hsCompanies.some((c) => c.companyId === account.hubspotCompanyId) && (
                  <option value={account.hubspotCompanyId}>{account.name}</option>
                )}
              {hsCompanies.map((c) => (
                <option key={c.companyId} value={c.companyId}>
                  {c.name}
                </option>
              ))}
            </select>
          </td>
        )}
        <td className="py-2 pr-4">
          <span
            className={
              account.status === "suspended" ? "text-danger" : "text-accent-strong"
            }
          >
            {account.status}
          </span>
        </td>
        <td className="py-2">
          <button className="btn-ghost !px-2 !py-1 text-xs" onClick={toggle}>
            {open ? "Close" : busy ? "…" : "Manage"}
          </button>
        </td>
      </tr>
      {error && !open && (
        <tr className="border-b border-line/50">
          <td colSpan={colSpan} className="px-4 pb-3">
            <Banner tone="error">{error}</Banner>
          </td>
        </tr>
      )}
      {open && (
        <tr className="border-b border-line/50 bg-black/10">
          <td colSpan={colSpan} className="px-4 py-3">
            {error && <Banner tone="error">{error}</Banner>}
            {notice && <Banner tone="success">{notice}</Banner>}

            {/* Account controls */}
            <div className="mb-4 rounded-lg border border-line/60 p-3">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="text-[11px] uppercase tracking-wide text-ink-dim">Account</span>
                <div className="ml-auto flex flex-wrap gap-2">
                  {!editing && (
                    <button
                      className="btn-ghost !px-2 !py-1 text-xs"
                      onClick={() => {
                        setEditName(account.name);
                        setEditLines(account.releaseLines);
                        setEditing(true);
                      }}
                    >
                      Edit
                    </button>
                  )}
                  {account.status === "suspended" ? (
                    <button
                      className="btn-ghost !px-2 !py-1 text-xs"
                      disabled={savingAcct}
                      onClick={() => void setStatus("active")}
                    >
                      {savingAcct ? "…" : "Activate"}
                    </button>
                  ) : (
                    <button
                      className="btn-ghost !px-2 !py-1 text-xs text-amber-400 hover:text-amber-300"
                      disabled={savingAcct}
                      onClick={() => void setStatus("suspended")}
                    >
                      {savingAcct ? "…" : "Suspend"}
                    </button>
                  )}
                  {hasBucket ? (
                    <button
                      className="btn-ghost !px-2 !py-1 text-xs text-red-400 hover:text-red-300"
                      disabled={bucketBusy}
                      onClick={() => void bucketAction("delete")}
                    >
                      {bucketBusy ? "…" : "Delete bucket"}
                    </button>
                  ) : (
                    <button
                      className="btn-ghost !px-2 !py-1 text-xs"
                      disabled={bucketBusy}
                      onClick={() => void bucketAction("provision")}
                    >
                      {bucketBusy ? "…" : "Provision bucket"}
                    </button>
                  )}
                  <button
                    className="btn-ghost !px-2 !py-1 text-xs text-red-400 hover:text-red-300"
                    disabled={deleting}
                    onClick={() => void deleteAccount()}
                  >
                    {deleting ? "Deleting…" : "Delete account"}
                  </button>
                </div>
              </div>
              {editing && (
                <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                  <label className="block text-sm">
                    <span className="mb-1 block text-ink-muted">Company name</span>
                    <input
                      className="input"
                      value={editName}
                      disabled={savingAcct}
                      onChange={(e) => setEditName(e.target.value)}
                    />
                    <div className="mt-2 flex flex-wrap gap-4">
                      {ALL_LINES.map((l) => (
                        <label key={l} className="flex items-center gap-2 text-sm text-ink-muted">
                          <input
                            type="checkbox"
                            checked={editLines.includes(l)}
                            disabled={savingAcct}
                            onChange={(e) =>
                              setEditLines((cur) =>
                                e.target.checked ? [...cur, l] : cur.filter((x) => x !== l),
                              )
                            }
                          />
                          <span className="font-mono text-xs">{l}</span>
                        </label>
                      ))}
                    </div>
                  </label>
                  <div className="flex gap-2">
                    <button
                      className="btn-primary !px-3 !py-1 text-xs"
                      disabled={savingAcct || !editName.trim()}
                      onClick={() => void saveAccount()}
                    >
                      {savingAcct ? "Saving…" : "Save"}
                    </button>
                    <button
                      className="btn-ghost !px-3 !py-1 text-xs"
                      disabled={savingAcct}
                      onClick={() => setEditing(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Invite user */}
            <div className="mb-4 rounded-lg border border-line/60 p-3">
              <span className="mb-2 block text-[11px] uppercase tracking-wide text-ink-dim">
                Invite user to this account
              </span>
              {invLink && (
                <Banner tone="success">
                  <div className="flex items-center gap-2">
                    <span>Invited. A set-password email was sent. One-time link:</span>
                    <input className="input flex-1 font-mono text-xs" readOnly value={invLink} />
                    <button
                      className="btn-ghost !px-2 !py-1 text-xs"
                      onClick={() => void navigator.clipboard?.writeText(invLink)}
                    >
                      Copy
                    </button>
                  </div>
                </Banner>
              )}
              <div className="flex flex-wrap items-end gap-2">
                <label className="block text-sm">
                  <span className="mb-1 block text-ink-muted">Email</span>
                  <input
                    type="email"
                    className="input !py-1 text-xs"
                    value={invEmail}
                    disabled={inviting}
                    onChange={(e) => setInvEmail(e.target.value)}
                  />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block text-ink-muted">Name (optional)</span>
                  <input
                    className="input !py-1 text-xs"
                    value={invName}
                    disabled={inviting}
                    onChange={(e) => setInvName(e.target.value)}
                  />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block text-ink-muted">Role</span>
                  <select
                    className="input !py-1 text-xs"
                    value={invRole}
                    disabled={inviting}
                    onChange={(e) => setInvRole(e.target.value)}
                  >
                    <option value="member">member</option>
                    <option value="account_admin">account_admin</option>
                  </select>
                </label>
                <button
                  className="btn-primary !px-3 !py-1 text-xs"
                  disabled={inviting || !invEmail.trim()}
                  onClick={() => void invite()}
                >
                  {inviting ? "Inviting…" : "Invite"}
                </button>
              </div>
            </div>

            {/* Users */}
            {busy && <p className="text-ink-muted">Loading users…</p>}
            {!busy && users && users.length === 0 && (
              <p className="text-ink-muted">No users assigned to this account yet.</p>
            )}
            {!busy && users && users.length > 0 && (
              <table className="w-full text-left">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wide text-ink-dim">
                    <th className="py-1 pr-4">Email</th>
                    <th className="py-1 pr-4">Name</th>
                    <th className="py-1 pr-4">Role</th>
                    <th className="py-1 pr-4">Account</th>
                    <th className="py-1 pr-4">Status</th>
                    <th className="py-1">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.contactId}>
                      <td className="py-1 pr-4 font-mono">{u.email}</td>
                      <td className="py-1 pr-4">
                        {[u.firstName, u.lastName].filter(Boolean).join(" ") || "—"}
                      </td>
                      <td className="py-1 pr-4">
                        {u.contactId.startsWith("local_") ? (
                          <select
                            className="input !py-0.5 text-xs"
                            value={u.role}
                            disabled={actingId === u.contactId}
                            onChange={(e) => void changeRole(u, e.target.value)}
                          >
                            <option value="member">member</option>
                            <option value="account_admin">account_admin</option>
                          </select>
                        ) : (
                          <span className="font-mono">{u.role}</span>
                        )}
                      </td>
                      <td className="py-1 pr-4">
                        {u.contactId.startsWith("local_") ? (
                          <select
                            className="input !py-0.5 text-xs"
                            value={account.companyId}
                            disabled={actingId === u.contactId}
                            onChange={(e) => void moveUser(u, e.target.value)}
                          >
                            {allAccounts.map((a) => (
                              <option key={a.companyId} value={a.companyId}>
                                {a.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span>{account.name}</span>
                        )}
                      </td>
                      <td className="py-1 pr-4">{u.status}</td>
                      <td className="py-1">
                        <div className="flex gap-2">
                          {u.status === "deactivated" ? (
                            <button
                              className="btn-ghost !px-2 !py-0.5 text-xs"
                              disabled={actingId === u.contactId}
                              onClick={() => void act(u, "reactivate")}
                            >
                              {actingId === u.contactId ? "…" : "Reactivate"}
                            </button>
                          ) : (
                            <button
                              className="btn-ghost !px-2 !py-0.5 text-xs"
                              disabled={actingId === u.contactId}
                              onClick={() => void act(u, "deactivate")}
                            >
                              {actingId === u.contactId ? "…" : "Deactivate"}
                            </button>
                          )}
                          <button
                            className="btn-ghost !px-2 !py-0.5 text-xs"
                            disabled={actingId === u.contactId}
                            onClick={() => void resetPassword(u)}
                          >
                            Reset password
                          </button>
                          <button
                            className="btn-ghost !px-2 !py-0.5 text-xs text-red-400 hover:text-red-300"
                            disabled={actingId === u.contactId}
                            onClick={() => void act(u, "delete")}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function PendingRequests({ api }: { api: Api }) {
  const [requests, setRequests] = useState<AccessRequest[] | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [approved, setApproved] = useState<{ email: string; actionLink: string } | null>(null);

  function load() {
    setError(null);
    api
      .listAccessRequests()
      .then((r) => setRequests(r.requests))
      .catch((err) =>
        setError(err instanceof ApiError ? err.message : "Could not load access requests."),
      );
    api
      .listAccounts()
      .then((r) => setAccounts(r.accounts))
      .catch(() => setAccounts([]));
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function decide(id: string, kind: "approve" | "deny") {
    setBusyId(id);
    setError(null);
    try {
      if (kind === "approve") {
        const companyId = picked[id] || undefined;
        const res = await api.approveAccessRequest(id, companyId ? { companyId } : undefined);
        setApproved({ email: res.email, actionLink: res.actionLink });
      } else {
        await api.denyAccessRequest(id);
      }
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "The decision could not be recorded.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Card>
      <div className="mb-4 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-ink">Access requests</h2>
          <p className="text-xs text-ink-dim">
            Self-service requests awaiting approval. Approve into a new dedicated account or associate
            to an existing one.
          </p>
        </div>
        <button className="btn-ghost" onClick={load}>
          Refresh
        </button>
      </div>
      {error && <Banner tone="error">{error}</Banner>}
      {approved && (
        <Banner tone="success">
          <div className="space-y-1">
            <p>
              Approved <span className="font-mono">{approved.email}</span>. A set-password email was
              sent. If it doesn’t arrive, copy this one-time link to send them manually:
            </p>
            <div className="flex items-center gap-2">
              <input className="input flex-1 font-mono text-xs" readOnly value={approved.actionLink} />
              <button
                className="btn-ghost !px-2 !py-1 text-xs"
                onClick={() => void navigator.clipboard?.writeText(approved.actionLink)}
              >
                Copy
              </button>
              <button className="btn-ghost !px-2 !py-1 text-xs" onClick={() => setApproved(null)}>
                Dismiss
              </button>
            </div>
          </div>
        </Banner>
      )}
      {requests && requests.length === 0 && (
        <p className="text-sm text-ink-muted">No pending requests.</p>
      )}
      {requests && requests.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-line text-xs uppercase tracking-wide text-ink-dim">
                <th className="py-2 pr-4">Requested</th>
                <th className="py-2 pr-4">Email</th>
                <th className="py-2 pr-4">Organization</th>
                <th className="py-2 pr-4">Associate to account</th>
                <th className="py-2">Decision</th>
              </tr>
            </thead>
            <tbody className="text-sm text-ink-muted">
              {requests.map((r) => (
                <tr key={r.id} className="border-b border-line/50">
                  <td className="py-2 pr-4 whitespace-nowrap font-mono text-xs">
                    {new Date(r.createdAt).toLocaleString()}
                  </td>
                  <td className="py-2 pr-4 font-mono text-xs">{r.email}</td>
                  <td className="py-2 pr-4">{r.company ?? "—"}</td>
                  <td className="py-2 pr-4">
                    <select
                      className="input !py-1 text-xs"
                      value={picked[r.id] ?? ""}
                      disabled={busyId === r.id}
                      onChange={(e) => setPicked((cur) => ({ ...cur, [r.id]: e.target.value }))}
                    >
                      <option value="">New dedicated account</option>
                      {accounts.map((a) => (
                        <option key={a.companyId} value={a.companyId}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2">
                    <div className="flex gap-2">
                      <button
                        className="btn-primary !px-3 !py-1 text-xs"
                        disabled={busyId === r.id}
                        onClick={() => void decide(r.id, "approve")}
                      >
                        {busyId === r.id ? "…" : "Approve"}
                      </button>
                      <button
                        className="btn-ghost !px-3 !py-1 text-xs"
                        disabled={busyId === r.id}
                        onClick={() => void decide(r.id, "deny")}
                      >
                        Deny
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
